/**
 * AdminShell — role-gated wrapper for the district admin console.
 *
 * Renders a sidebar nav (Overview / Invites / Members / Audit Log /
 * Settings) plus the active admin sub-screen. The shell is itself
 * a top-level route on the SPA: `/admin/district/...`.
 *
 * Auth gate:
 *  - User must be signed in (delegated to the global AppShell).
 *  - User must have role='admin' OR 'billing_admin' on at least
 *    one `organization_memberships` row. Otherwise we show a
 *    friendly "you're not an admin" message — never a 403.
 *
 * SSO via WorkOS is layered later when the first district deal
 * closes (per docs/AUTH-DB-STACK-COMPARISON-2026.md). For now we
 * share the Supabase magic-link auth that the SLP tier uses.
 */
import { useEffect, useState } from 'react';
import type { ReactElement } from 'react';
import { supabase } from '../../lib/supabase/client';

export type AdminSubScreen =
  | { kind: 'overview' }
  | { kind: 'invites' }
  | { kind: 'members' }
  | { kind: 'audit' }
  | { kind: 'settings' };

interface OrgInfo {
  id: string;
  name: string;
  state: string | null;
}

interface Props {
  initialSubScreen?: AdminSubScreen;
  onNavigate: (subScreen: AdminSubScreen) => void;
  children: React.ReactNode;
}

function readSubScreen(pathname: string): AdminSubScreen {
  if (pathname.endsWith('/admin/district/invites')) return { kind: 'invites' };
  if (pathname.endsWith('/admin/district/members')) return { kind: 'members' };
  if (pathname.endsWith('/admin/district/audit')) return { kind: 'audit' };
  if (pathname.endsWith('/admin/district/settings')) return { kind: 'settings' };
  return { kind: 'overview' };
}

export function AdminShell({
  initialSubScreen,
  onNavigate,
  children,
}: Props): ReactElement {
  const [subScreen, setSubScreen] = useState<AdminSubScreen>(
    initialSubScreen ?? readSubScreen(window.location.pathname),
  );
  const [org, setOrg] = useState<OrgInfo | null>(null);
  const [role, setRole] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        // Look up the admin's first organization_membership where
        // role is admin or billing_admin.
        const { data: user } = await supabase.auth.getUser();
        if (!user?.user) {
          setError('not_signed_in');
          setLoading(false);
          return;
        }
        const { data, error: lookupErr } = await supabase
          .from('organization_memberships')
          .select('role, organizations(id, name, state)')
          .eq('user_id', user.user.id)
          .in('role', ['admin', 'billing_admin'])
          .limit(1)
          .maybeSingle();
        if (cancelled) return;
        if (lookupErr) {
          setError(lookupErr.message);
        } else if (!data) {
          setError('not_admin');
        } else {
          const row = data as unknown as {
            role: string;
            organizations: OrgInfo | null;
          };
          setRole(row.role);
          setOrg(row.organizations ?? null);
        }
      } catch (err) {
        if (!cancelled) setError(err instanceof Error ? err.message : 'lookup_failed');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  function navigateTo(next: AdminSubScreen): void {
    setSubScreen(next);
    onNavigate(next);
  }

  if (loading) {
    return (
      <main className="screen boot">
        <p className="tagline">Loading district console…</p>
      </main>
    );
  }

  if (error === 'not_signed_in') {
    return (
      <main className="screen admin">
        <header className="screen-header">
          <h1>District console</h1>
          <p className="tagline">Sign in to access the district admin console.</p>
        </header>
      </main>
    );
  }

  if (error === 'not_admin' || !org) {
    return (
      <main className="screen admin">
        <header className="screen-header">
          <h1>District console</h1>
          <p className="tagline">
            You don&rsquo;t have district-admin access on EffectiveSLP.
            Ask your district&rsquo;s IT admin to invite you with the
            &ldquo;admin&rdquo; role.
          </p>
        </header>
      </main>
    );
  }

  const navItems: Array<{ key: AdminSubScreen['kind']; label: string }> = [
    { key: 'overview', label: 'Overview' },
    { key: 'invites', label: 'Invites' },
    { key: 'members', label: 'Members' },
    { key: 'audit', label: 'Audit log' },
    { key: 'settings', label: 'Settings' },
  ];

  return (
    <main className="screen admin">
      <header className="screen-header">
        <h1>District console</h1>
        <p className="tagline">
          {org.name}
          {org.state ? ` · ${org.state}` : ''} · Signed in as{' '}
          <span className="chip">{role}</span>
        </p>
      </header>

      <nav className="admin-nav" aria-label="District admin navigation">
        {navItems.map((item) => (
          <button
            key={item.key}
            type="button"
            className={
              subScreen.kind === item.key
                ? 'admin-nav-link active'
                : 'admin-nav-link'
            }
            onClick={() => navigateTo({ kind: item.key } as AdminSubScreen)}
          >
            {item.label}
          </button>
        ))}
      </nav>

      <section className="admin-pane">{children}</section>
    </main>
  );
}